import { Button } from "@/components/ui/button";
import {
  CHECKOUT_FLAG_KEYS,
  DEFAULT_SHOP_BUSINESS_TYPE,
  NAV_ITEM_KEYS,
  POS_SETTING_KEYS,
  SHOP_BUSINESS_TYPES,
  type ShopBusinessType,
  type ShopSettings,
} from "@mm-sales/shared";
import { useState } from "react";

/**
 * The demo shop's settings, mirroring MyManager's Shop+ settings screens
 * (Shop → Settings): store, register, POS screens and payments, online
 * checkout, tips. Everything left untouched keeps MyManager's default. The
 * provisioner applies the result to the demo ShopV2 / PosSettings.
 */
const LABEL: Record<string, string> = {
  // posSettings
  enableInStore: "In-store orders",
  enablePickup: "Pickup orders",
  enableDelivery: "Delivery orders",
  enableTableSelection: "Table selection",
  enableCustomerSelection: "Customer selection",
  closeOrderAfterPayment: "Close order after payment",
  // navItemsState (POS screens & payments)
  orders: "Orders",
  tables: "Tables",
  kds: "Kitchen display",
  cfd: "Customer display",
  reservation: "Reservations",
  giftCard: "Gift cards (redeem)",
  sellGiftCard: "Sell gift cards",
  splitPayment: "Split payment",
  tips: "Tips",
  timeclock: "Time clock",
  checkin: "Check-in",
  memberships: "Memberships",
  courses: "Courses",
  eightySix: "86 list",
  cashDrawer: "Cash drawer",
  payCash: "Pay: cash",
  payCard: "Pay: card",
  payTerminal: "Pay: terminal",
  payCheck: "Pay: check",
  chargeAch: "Pay: ACH",
  scanCustomer: "Scan customer",
  noSale: "No sale",
  payout: "Payouts",
  reports: "Reports",
  transactions: "Transactions",
  batches: "Batches",
  products: "Products",
  addProduct: "Add product",
  services: "Services",
  addEmployee: "Add employee",
  mixingStation: "Mixing station",
  // checkoutSettings flags
  requireLogin: "Require login to order",
  trackOrder: "Order tracking",
  autoSendReceiptSms: "Text receipt automatically",
  autoSendOrderConfirmationEmail: "Email order confirmation",
  giftCardsEnabled: "Gift cards online",
  addressAutoCompletion: "Address auto-complete",
  validateShippingAddress: "Validate shipping address",
};

const TIMEZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Phoenix",
  "America/Los_Angeles",
  "America/Anchorage",
  "Pacific/Honolulu",
];

type Flags = Record<string, boolean | undefined>;

function Tri({
  value,
  onChange,
  label,
}: {
  value: boolean | undefined;
  onChange: (v: boolean | undefined) => void;
  label: string;
}) {
  // Three states: default (MyManager's), on, off — so a demo only overrides what the rep chose.
  const next = () => onChange(value === undefined ? true : value ? false : undefined);
  return (
    <button
      type="button"
      onClick={next}
      className="flex w-full items-center justify-between rounded-md border px-2.5 py-1.5 text-left text-xs hover:bg-muted"
      title="Click to cycle: default → on → off"
    >
      <span>{label}</span>
      <span
        className={
          value === undefined
            ? "text-muted-foreground"
            : value
              ? "font-semibold text-success"
              : "font-semibold text-destructive"
        }
      >
        {value === undefined ? "default" : value ? "on" : "off"}
      </span>
    </button>
  );
}

function FlagGrid({
  keys,
  value,
  onChange,
}: {
  keys: readonly string[];
  value: Flags;
  onChange: (v: Flags) => void;
}) {
  return (
    <div className="grid gap-1.5 sm:grid-cols-2 lg:grid-cols-3">
      {keys.map((k) => (
        <Tri
          key={k}
          label={LABEL[k] ?? k}
          value={value[k]}
          onChange={(v) => {
            const n = { ...value };
            if (v === undefined) delete n[k];
            else n[k] = v;
            onChange(n);
          }}
        />
      ))}
    </div>
  );
}

function Section({
  title,
  hint,
  children,
}: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-1.5">
        <div className="text-sm font-semibold">{title}</div>
        {hint ? <div className="text-xs text-muted-foreground">{hint}</div> : null}
      </div>
      {children}
    </div>
  );
}

const numList = (s: string) =>
  s
    .split(/[,\s]+/)
    .map((x) => Number(x))
    .filter((n) => Number.isFinite(n) && n >= 0 && n <= 100);

export function ShopSettingsForm({
  value,
  onSave,
  saving,
}: {
  value: ShopSettings | null;
  onSave: (v: ShopSettings | null) => void;
  saving?: boolean;
}) {
  const [s, setS] = useState<ShopSettings>(value ?? {});
  const [tipText, setTipText] = useState(
    (s.checkoutSettings?.tipping?.tipOptions ?? []).join(", "),
  );
  const [posTipText, setPosTipText] = useState((s.posTipPercentages ?? []).join(", "));
  const co = s.checkoutSettings ?? {};
  const setCo = (patch: Partial<NonNullable<ShopSettings["checkoutSettings"]>>) =>
    setS({ ...s, checkoutSettings: { ...co, ...patch } });
  const touched = Object.keys(s).length > 0;

  const save = () => {
    const out: ShopSettings = { ...s };
    const tips = numList(tipText);
    const posTips = numList(posTipText).slice(0, 4);
    out.checkoutSettings = {
      ...co,
      ...(tips.length || co.tipping?.show !== undefined
        ? { tipping: { ...(co.tipping ?? {}), ...(tips.length ? { tipOptions: tips } : {}) } }
        : {}),
    };
    if (!Object.keys(out.checkoutSettings).length) out.checkoutSettings = undefined;
    if (posTips.length) out.posTipPercentages = posTips;
    else out.posTipPercentages = undefined;
    onSave(Object.values(out).some((v) => v !== undefined) ? out : null);
  };

  return (
    <div className="space-y-5">
      <Section
        title="Store"
        hint="Business type, time zone and how the online store starts an order."
      >
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          <label className="text-xs">
            <span className="mb-1 block text-muted-foreground">Business type</span>
            <select
              className="h-9 w-full rounded-md border bg-background px-2 text-sm"
              value={s.businessType ?? DEFAULT_SHOP_BUSINESS_TYPE}
              onChange={(e) => setS({ ...s, businessType: e.target.value as ShopBusinessType })}
            >
              {SHOP_BUSINESS_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            <span className="mt-1 block text-[11px] text-muted-foreground">
              Drives the POS mode and the Mixing Station variant (Services → Color Bar, Restaurants
              &amp; Bars → Pour Station). Starts on Restaurants &amp; Bars; change it after the
              import.
            </span>
          </label>
          <label className="text-xs">
            <span className="mb-1 block text-muted-foreground">Time zone</span>
            <select
              className="h-9 w-full rounded-md border bg-background px-2 text-sm"
              value={s.timezone ?? ""}
              onChange={(e) => setS({ ...s, timezone: e.target.value || undefined })}
            >
              <option value="">Default (Eastern)</option>
              {TIMEZONES.map((tz) => (
                <option key={tz} value={tz}>
                  {tz.replace("America/", "").replace("Pacific/", "").replace("_", " ")}
                </option>
              ))}
            </select>
          </label>
          <div className="space-y-1.5 sm:col-span-1 lg:col-span-2 lg:grid lg:grid-cols-2 lg:gap-1.5 lg:space-y-0">
            <Tri
              label="Ask pickup / delivery first"
              value={s.showDeliveryMethodAtStart}
              onChange={(v) => setS({ ...s, showDeliveryMethodAtStart: v })}
            />
            <Tri
              label="Delivery on the online store"
              value={s.onlineStoreDeliveryEnabled}
              onChange={(v) => setS({ ...s, onlineStoreDeliveryEnabled: v })}
            />
            <Tri
              label="Auto-close paid orders"
              value={s.autoCloseOrder}
              onChange={(v) => setS({ ...s, autoCloseOrder: v })}
            />
            <Tri
              label="Pass card fees to customer"
              value={s.passOnCC}
              onChange={(v) => setS({ ...s, passOnCC: v })}
            />
          </div>
        </div>
      </Section>

      <Section title="Register" hint="What the POS offers when an order starts.">
        <FlagGrid
          keys={POS_SETTING_KEYS}
          value={(s.posSettings ?? {}) as Flags}
          onChange={(v) => setS({ ...s, posSettings: v })}
        />
      </Section>

      <Section
        title="POS screens & payments"
        hint="Which screens and payment types the register shows."
      >
        <FlagGrid
          keys={NAV_ITEM_KEYS}
          value={(s.navItemsState ?? {}) as Flags}
          onChange={(v) => setS({ ...s, navItemsState: v })}
        />
      </Section>

      <Section title="Online checkout" hint="The customer-facing checkout on the website and app.">
        <FlagGrid
          keys={CHECKOUT_FLAG_KEYS}
          value={co as Flags}
          onChange={(v) =>
            setS({
              ...s,
              checkoutSettings: {
                ...co,
                ...v,
                ...Object.fromEntries(
                  CHECKOUT_FLAG_KEYS.filter((k) => !(k in v)).map((k) => [k, undefined]),
                ),
              },
            })
          }
        />
        <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          <label className="text-xs">
            <span className="mb-1 block text-muted-foreground">Contact method</span>
            <select
              className="h-9 w-full rounded-md border bg-background px-2 text-sm"
              value={co.contactMethod ?? ""}
              onChange={(e) =>
                setCo({
                  contactMethod: (e.target.value || undefined) as
                    | "email"
                    | "phone"
                    | "both"
                    | undefined,
                })
              }
            >
              <option value="">Default</option>
              <option value="email">Email</option>
              <option value="phone">Phone</option>
              <option value="both">Both</option>
            </select>
          </label>
          <Tri
            label="Show tipping"
            value={co.tipping?.show}
            onChange={(v) => setCo({ tipping: { ...(co.tipping ?? {}), show: v } })}
          />
          <label className="text-xs">
            <span className="mb-1 block text-muted-foreground">Tip options (%)</span>
            <input
              className="h-9 w-full rounded-md border bg-background px-2 text-sm"
              placeholder="15, 18, 20, 25"
              value={tipText}
              onChange={(e) => setTipText(e.target.value)}
            />
          </label>
          <Tri
            label="Discount codes"
            value={co.discountCode?.show}
            onChange={(v) => setCo({ discountCode: { show: v } })}
          />
          <Tri
            label="Scheduled pickups"
            value={co.futurePickups?.enabled}
            onChange={(v) => setCo({ futurePickups: { ...(co.futurePickups ?? {}), enabled: v } })}
          />
          <label className="text-xs">
            <span className="mb-1 block text-muted-foreground">Pickup slot (minutes)</span>
            <input
              type="number"
              min={5}
              max={120}
              className="h-9 w-full rounded-md border bg-background px-2 text-sm"
              value={co.futurePickups?.slotDuration ?? ""}
              onChange={(e) =>
                setCo({
                  futurePickups: {
                    ...(co.futurePickups ?? {}),
                    slotDuration: e.target.value ? Number(e.target.value) : undefined,
                  },
                })
              }
            />
          </label>
          <label className="text-xs">
            <span className="mb-1 block text-muted-foreground">Orders per slot</span>
            <input
              type="number"
              min={1}
              max={100}
              className="h-9 w-full rounded-md border bg-background px-2 text-sm"
              value={co.futurePickups?.maxOrdersPerSlot ?? ""}
              onChange={(e) =>
                setCo({
                  futurePickups: {
                    ...(co.futurePickups ?? {}),
                    maxOrdersPerSlot: e.target.value ? Number(e.target.value) : undefined,
                  },
                })
              }
            />
          </label>
          <Tri
            label="Pre-authorize cards"
            value={co.preAuth?.show}
            onChange={(v) => setCo({ preAuth: { ...(co.preAuth ?? {}), show: v } })}
          />
        </div>
      </Section>

      <Section title="Register tips" hint="The four tip buttons on the POS payment screen.">
        <input
          className="h-9 w-full max-w-xs rounded-md border bg-background px-2 text-sm"
          placeholder="15, 18, 20, 25"
          value={posTipText}
          onChange={(e) => setPosTipText(e.target.value)}
        />
      </Section>

      <div className="flex items-center justify-between gap-2 border-t pt-3">
        <span className="text-xs text-muted-foreground">
          {touched
            ? "Only the values you changed are applied; the rest stay MyManager defaults."
            : "Nothing overridden — the demo uses MyManager defaults."}
        </span>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setS({});
              setTipText("");
              setPosTipText("");
              onSave(null);
            }}
          >
            Reset to defaults
          </Button>
          <Button size="sm" loading={saving} onClick={save}>
            Save settings
          </Button>
        </div>
      </div>
    </div>
  );
}
